import { customInstance } from '@/api/mutator/custom-instance';
import type { ObservabilityLogsRetrieve2200 } from '@/gen-api/models';

export type ObservabilityLogDetail = ObservabilityLogsRetrieve2200;

export type ObservabilityLogsBatchResponse = {
  results: ObservabilityLogDetail[];
  missing_ids: string[];
};

type FetchOptions = {
  collection?: string;
  /** When true, backend may Redis-cache the finalized payload (workspace-scoped). */
  stable?: boolean;
  signal?: AbortSignal;
};

function buildParams(options?: FetchOptions): Record<string, string | boolean> | undefined {
  if (!options?.collection && !options?.stable) return undefined;
  return {
    ...(options.collection ? { collection: options.collection } : {}),
    ...(options.stable ? { stable: true } : {}),
  };
}

/**
 * Fetch a single observability parent doc.
 * Prefer ``collection: 'CHAT'`` when the caller knows the id lives in chat
 * (User Activity / Eval Studio) so the backend skips developer/system lookups.
 * Pass ``stable: true`` for finalized docs so Redis can cache the response.
 */
export function fetchObservabilityLogDetail(logId: string, options?: FetchOptions): Promise<ObservabilityLogDetail> {
  return customInstance<ObservabilityLogDetail>({
    url: `${import.meta.env.VITE_API_URL}/observability/logs/${logId}/`,
    method: 'GET',
    params: buildParams(options),
    signal: options?.signal,
  });
}

export function getObservabilityLogDetailQueryKey(logId: string, collection?: string, stable?: boolean) {
  return [
    `${import.meta.env.VITE_API_URL}/observability/logs/${logId}/`,
    collection ?? 'all',
    stable ? 'stable' : 'live',
  ] as const;
}

/**
 * Fetch many observability parent docs in one request (full metadata preserved).
 * Used by Eval Studio Flow Chart / Chat Preview for multi-turn runs.
 */
export function fetchObservabilityLogsBatch(
  ids: string[],
  options?: FetchOptions
): Promise<ObservabilityLogsBatchResponse> {
  const uniqueIds = Array.from(new Set(ids.filter(Boolean)));
  if (!uniqueIds.length) {
    return Promise.resolve({ results: [], missing_ids: [] });
  }

  return customInstance<ObservabilityLogsBatchResponse>({
    url: `${import.meta.env.VITE_API_URL}/observability/logs/batch/`,
    method: 'GET',
    params: {
      id: uniqueIds,
      ...buildParams(options),
    },
    signal: options?.signal,
  });
}

export function getObservabilityLogsBatchQueryKey(ids: string[], collection?: string, stable?: boolean) {
  return [
    `${import.meta.env.VITE_API_URL}/observability/logs/batch/`,
    collection ?? 'all',
    stable ? 'stable' : 'live',
    [...ids].sort().join(','),
  ] as const;
}
